import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { ApiResponse } from '../interfaces/common.interfaces';
import {
  ClientPaymentRequest,
  ClientPaymentResponse,
  AgingProducerRequest,
  AgingProducerResponse,
  StatementRequest,
  StatementResponse,
  InvoiceRequest,
  InvoiceResponse,
} from '../interfaces/gestioncobros.interfaces';

/**
 * Servicio para gestionar las operaciones relacionadas con cobros
 */
@Injectable({
  providedIn: 'root',
})
export class GestionCobrosService {
  private apiUrl = environment.int365_dom + '/api/v1';

  constructor(private http: HttpClient, private authService: AuthService) {}

  /**
   * Obtiene los pagos de clientes según los criterios especificados
   * @param request Objeto con los parámetros de búsqueda de pagos
   * @returns Observable con la respuesta de pagos de clientes
   */
  getClientPayments(
    request: ClientPaymentRequest
  ): Observable<HttpResponse<ApiResponse<ClientPaymentResponse[]>>> {
    return this.http.get<ApiResponse<ClientPaymentResponse[]>>(
      `${this.apiUrl}/ClientPayments/payments`,
      {
        ...this.getHttpOptions(request),
        observe: 'response',
        responseType: 'json',
      }
    );
  }

  /**
   * Obtiene los recibos de cobros según los criterios especificados
   * @param request Objeto con los parámetros de búsqueda de recibos
   * @returns Observable con la respuesta de recibos
   */
  getClientPaymentsInvoice(
    request: InvoiceRequest
  ): Observable<ApiResponse<InvoiceResponse[]>> {
    return this.http
      .get<ApiResponse<InvoiceResponse[]>>(
        `${this.apiUrl}/ClientPayments/invoice`,
        this.getHttpOptions(request)
      )
      .pipe(
        map((response: any) => {
          try {
            return response;
          } catch (error) {
            console.error('Error en la obtención de recibos de cobros:', error);
            if (response.statusCode === 404) {
              throw { status: 404, error: response };
            }

            throw error;
          }
        })
      );
  }

  /**
   * Obtiene la antigüedad de los productores según los criterios especificados
   * @param request Objeto con los parámetros de búsqueda de antigüedad
   * @returns Observable con la respuesta de antigüedad de productores
   */
  getAgingProducers(
    request: AgingProducerRequest
  ): Observable<HttpResponse<ApiResponse<AgingProducerResponse[]>>> {
    return this.http.get<ApiResponse<AgingProducerResponse[]>>(
      `${this.apiUrl}/BalanceAging/agingproducer`,
      {
        ...this.getHttpOptions(request),
        observe: 'response',
        responseType: 'json',
      }
    );
  }

  /**
   * Obtiene los estados de cuenta según los criterios especificados
   * @param request Objeto con los parámetros de búsqueda de estados de cuenta
   * @returns Observable con la respuesta de estados de cuenta
   */
  getStatements(
    request: StatementRequest
  ): Observable<HttpResponse<ApiResponse<StatementResponse[]>>> {
    return this.http.get<ApiResponse<StatementResponse[]>>(
      `${this.apiUrl}/Statement/statements`,
      {
        ...this.getHttpOptions(request),
        observe: 'response',
        responseType: 'json',
      }
    );
  }

  /**
   * Configura las opciones HTTP incluyendo headers y parámetros
   * @param params Parámetros opcionales para la petición
   * @returns Objeto con las opciones HTTP configuradas
   * @throws Error si no se encuentra el token de autenticación
   */
  private getHttpOptions(params?: any) {
    const token = this.authService.getToken();
    if (!token) throw new Error('No token found');

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return {
      headers,
      params: params
        ? new HttpParams({ fromObject: this.formatParams(params) })
        : undefined,
    };
  }

  /**
   * Formatea los parámetros para la petición HTTP
   * @param params Objeto con los parámetros a formatear
   * @returns Objeto con los parámetros formateados como strings
   */
  private formatParams(params: any): { [key: string]: string } {
    const formattedParams: { [key: string]: string } = {};

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (value instanceof Date) {
        formattedParams[key] = value.toISOString().split('T')[0];
      } else {
        formattedParams[key] = String(value);
      }
    });

    return formattedParams;
  }
}
